import { useTranslation } from 'react-i18next';
import { memo } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { DashboardLayout } from '@/components/ui/dashboard-layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useDashboardStats } from '@/hooks/use-dashboard';
import { usePortalSettings } from '@/hooks/use-settings';
import { useProjects } from '@/hooks/use-projects';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { cn, formatCurrency } from '@/lib/utils';
import { Skeleton, SkeletonStatCard } from '@/components/ui/skeleton';
import {
  FolderKanban,
  CheckSquare,
  Clock,
  TrendingUp,
  AlertCircle,
  ArrowRight,
  Ticket,
  Receipt,
  BarChart3,
  PieChart,
  Settings,
  TrendingDown,
  HeartPulse,
  Activity,
} from 'lucide-react';
import { useSiteMonitors } from '@/hooks/use-site-monitors';
import { useInvoices } from '@/hooks/use-invoices';
import { EmptyState } from '@/components/ui/empty-state';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import { Link, useNavigate } from 'react-router-dom';

const stageColors: Record<string, string> = {
  enquiry: 'bg-purple-500/10 text-purple-400',
  quote: 'bg-blue-500/10 text-blue-400',
  design: 'bg-yellow-500/10 text-yellow-400',
  develop: 'bg-orange-500/10 text-orange-400',
  review: 'bg-pink-500/10 text-pink-400',
  launch: 'bg-cyan-500/10 text-cyan-400',
  finalised: 'bg-dyndev-green/10 text-dyndev-green',
};

// ── Dashboard Stat Card (memoized to prevent re-renders when other cards change) ────
interface StatCardProps {
  stat: {
    label: string;
    value: string;
    icon: React.ComponentType<{ size?: number; className?: string }>;
    change: string;
    color: string;
    link?: string;
  };
}

const DashboardStatCard = memo(function DashboardStatCard({ stat }: StatCardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Card
      className={cn(
        'hover:shadow-lg hover:shadow-dyndev-green/5 transition-all',
        stat.link && 'cursor-pointer !cursor-pointer',
      )}
      onClick={() => {
        if (stat.link) navigate(stat.link);
      }}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <stat.icon size={24} className={stat.color} />
          <span className="text-xs text-[var(--text-secondary)] text-right">{stat.change}</span>
        </div>
        <p className="text-2xl font-bold mb-1">{stat.value}</p>
        <p className="text-sm text-[var(--text-secondary)]">
          {typeof stat.label === 'string' && stat.label.startsWith('dashboard.')
            ? t(stat.label)
            : stat.label}
        </p>
      </CardContent>
    </Card>
  );
});

export function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const isClient = user?.role === 'client';

  useDocumentTitle('Dashboard');

  const { data: statsData, isLoading: statsLoading } = useDashboardStats();

  if (isClient) {
    return (
      <DashboardLayout>
        <ClientDashboard />
      </DashboardLayout>
    );
  }

  const stats = [
    {
      label: 'dashboard.stats.activeProjects',
      value: statsLoading ? '-' : String(statsData?.activeProjectsCount ?? 0),
      icon: FolderKanban,
      change: `${statsData?.recentProjects?.length || 0} recent`,
      color: 'text-dyndev-green',
    },
    {
      label: 'dashboard.stats.pendingTasks',
      value: statsLoading ? '-' : String(statsData?.pendingTasksCount ?? 0),
      icon: CheckSquare,
      change: 'Not yet completed',
      color: 'text-yellow-400',
    },
    {
      label: 'Overdue Invoices',
      value: statsLoading ? '-' : String(statsData?.overdueInvoicesCount ?? 0),
      icon: TrendingDown,
      change: statsData?.overdueInvoicesCount > 0
        ? `${statsData?.sentInvoicesCount || 0} sent, £${(statsData?.totalOutstanding || 0).toFixed(0)} outstanding`
        : 'All clear',
      color: statsData?.overdueInvoicesCount > 0 ? 'text-red-400' : 'text-dyndev-green',
      link: '/invoices?status=overdue',
    },
    {
      label: 'dashboard.stats.revenueMtd',
      value: statsLoading ? '-' : formatCurrency(statsData?.totalRevenue ?? 0),
      icon: TrendingUp,
      change: 'Total revenue',
      color: 'text-dyndev-green',
    },
  ];

  const recentProjects = statsData?.recentProjects || [];
  const upcomingTasks = statsData?.upcomingTasks || [];

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-1 sm:mb-2 leading-tight">
          {t('dashboard.title', { name: '' })}
          <span className="text-dyndev-green block sm:inline"> {user?.name?.split(' ')[0]}</span>
        </h1>
        <p className="text-sm sm:text-base text-[var(--text-secondary)]">{t('dashboard.subtitle')}</p>
      </div>

      {/* Stats Grid */}
      {statsLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonStatCard key={i} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((stat) => (
            <DashboardStatCard key={stat.label} stat={stat} />
          ))}
        </div>
      )}

      {/* Charts */}
      {!statsLoading && (statsData?.stageDistribution?.length > 0 || statsData?.monthlyRevenue?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Project Stage Distribution */}
          {statsData?.stageDistribution?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <BarChart3 size={16} className="text-dyndev-green" />
                  <CardTitle className="text-sm">Projects by Stage</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={statsData.stageDistribution} barCategoryGap="20%">
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" opacity={0.3} />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="var(--text-secondary)" />
                      <YAxis tick={{ fontSize: 11 }} stroke="var(--text-secondary)" allowDecimals={false} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'var(--bg-secondary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '8px',
                          fontSize: '12px',
                        }}
                      />
                      <Bar dataKey="count" fill="#1ce783" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Monthly Revenue Trend */}
          {statsData?.monthlyRevenue?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <TrendingUp size={16} className="text-yellow-400" />
                  <CardTitle className="text-sm">Revenue Over Time</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={statsData.monthlyRevenue}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" opacity={0.3} />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="var(--text-secondary)" />
                      <YAxis tick={{ fontSize: 11 }} stroke="var(--text-secondary)" tickFormatter={(v: number) => `£${v}`} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'var(--bg-secondary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '8px',
                          fontSize: '12px',
                        }}
                        formatter={(value: any) => [`£${Number(value).toFixed(2)}`, 'Revenue']}
                      />
                      <Line
                        type="monotone"
                        dataKey="revenue"
                        stroke="#1ce783"
                        strokeWidth={2}
                        dot={{ fill: '#1ce783', r: 3 }}
                        activeDot={{ r: 5 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Projects */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-lg">{t('dashboard.recentProjects')}</CardTitle>
            <Button variant="link" size="sm" asChild className="gap-1">
              <Link to="/projects">
                {t('common.viewAll')} <ArrowRight size={14} />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center justify-between p-3">
                    <div className="space-y-1.5 flex-1">
                      <Skeleton className="h-4 w-2/3" />
                      <Skeleton className="h-3 w-1/3" />
                    </div>
                    <Skeleton className="h-5 w-14 rounded-full" />
                  </div>
                ))}
              </div>
            ) : recentProjects.length === 0 ? (
              <div className="py-4">
                <EmptyState
                  icon={FolderKanban}
                  title="No projects yet"
                  description="Your recent projects will appear here."
                />
              </div>
            ) : (
              <div className="space-y-2">
                {recentProjects.map((project: any) => (
                  <div
                    key={project.id}
                    onClick={() => navigate(`/projects/${project.id}`)}
                    className="flex items-center justify-between gap-2 p-3 rounded-lg hover:bg-[var(--bg-primary)] transition-colors cursor-pointer"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{project.name}</p>
                      <p className="text-xs text-[var(--text-secondary)] truncate">{project.client}</p>
                    </div>
                    <Badge variant="outline" className={`text-[10px] flex-shrink-0 ${stageColors[project.stage] || 'bg-gray-500/10 text-gray-400'}`}>
                      {project.stage}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Tasks */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-lg">{t('dashboard.upcomingTasks')}</CardTitle>
            <Button variant="link" size="sm" asChild className="gap-1">
              <Link to="/tasks">
                {t('common.viewAll')} <ArrowRight size={14} />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-start gap-3 p-3">
                    <Skeleton className="h-4 w-4 rounded-full mt-0.5 flex-shrink-0" />
                    <div className="flex-1 space-y-1.5">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                    <Skeleton className="h-3 w-12" />
                  </div>
                ))}
              </div>
            ) : upcomingTasks.length === 0 ? (
              <div className="py-4">
                <EmptyState
                  icon={CheckSquare}
                  title="No tasks yet"
                  description="Your upcoming tasks will appear here."
                />
              </div>
            ) : (
              <div className="space-y-2">
                {upcomingTasks.map((task: any) => (                    <div
                    key={task.id}
                    className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg hover:bg-[var(--bg-primary)] transition-colors"
                  >
                    <AlertCircle
                      size={14}
                      className={`${task.priority === 'high' || task.priority === 'urgent' ? 'text-red-400 mt-0.5 flex-shrink-0' : 'text-yellow-400 mt-0.5 flex-shrink-0'} sm:size-4`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-xs sm:text-sm truncate">{task.title}</p>
                      {task.project && (
                        <p className="text-[10px] sm:text-xs text-[var(--text-secondary)] truncate">{task.project}</p>
                      )}
                    </div>
                    <span className="text-[10px] sm:text-xs text-[var(--text-secondary)] whitespace-nowrap">{task.due}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}

function ClientDashboard() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { data: projectsData, isLoading } = useProjects({ limit: '5' });
  // Poll invoices while on the dashboard so a just-paid invoice (and the unpaid
  // deposit banner) updates automatically without a manual refresh.
  const { data: invoicesData } = useInvoices(undefined, { refetchInterval: 15_000 });
  const { data: settingsData } = usePortalSettings();
  const settings = settingsData?.data || {};
  const accentColor = settings.accentColor || '#1ce783';
  const projects = projectsData?.data || [];

  // Unpaid deposit invoice — deposit invoices are created with a single line item
  // whose description starts with "Deposit —". Clients never see draft invoices,
  // so any deposit invoice here is sent or overdue — i.e. still awaiting payment.
  // (awaiting_transfer is deliberately excluded: the client has already confirmed
  // a bank transfer, so the banner clears.)
  const unpaidDeposit = (invoicesData?.data || []).find((inv: any) =>
    ['sent', 'overdue'].includes(inv.status) &&
    (inv.lineItems || []).some((li: any) => (li.description || '').startsWith('Deposit —')),
  );
  const unpaidDepositProject = unpaidDeposit?.project?.name;

  const stageOrder = ['enquiry', 'quote', 'design', 'develop', 'review', 'launch', 'finalised'];
  const totalStageCount = stageOrder.length;

  return (
    <>
      {/* Branded welcome header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight">
          {t('dashboard.title', { name: '' })}
          <span className="text-dyndev-green block sm:inline"> {user?.name?.split(' ')[0]}</span>
        </h1>
        <p className="text-sm sm:text-base text-[var(--text-secondary)] ml-1">{t('dashboard.clientSubtitle')}</p>
      </div>

      {/* Unpaid deposit reminder banner */}
      {unpaidDeposit && (
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl border border-red-500/30 bg-red-500/5">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[var(--text-primary)]">
                Your deposit is due{unpaidDepositProject ? ` for ${unpaidDepositProject}` : ''} — {unpaidDeposit.invoiceNumber}
              </p>
              <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                {formatCurrency(unpaidDeposit.total)} · Work won't begin on this project until the deposit is paid.
              </p>
            </div>
          </div>
          <Link
            to={`/invoices?pay=${unpaidDeposit.id}`}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-red-500/90 hover:bg-red-500 text-white text-xs font-medium transition-colors flex-shrink-0"
          >
            <Receipt size={14} />
            Pay Now
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Projects section */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle>{t('dashboard.activeProject')}</CardTitle>
            <Button variant="link" size="sm" asChild className="gap-1">
              <Link to="/projects">
                {t('common.viewAll')} <ArrowRight size={14} />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 p-3 rounded-lg bg-[var(--bg-secondary)]">
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <Skeleton className="h-4 w-full sm:w-40" />
                      <Skeleton className="h-3 w-full sm:w-24" />
                    </div>
                    <Skeleton className="h-5 w-14 rounded-full flex-shrink-0" />
                  </div>
                ))}
              </div>
            ) : projects.length > 0 ? (
              <div className="space-y-3">
                {projects.map((p: any) => {
                  const stageIndex = stageOrder.indexOf(p.stage);
                  const progress = stageIndex >= 0 ? Math.round(((stageIndex + 1) / totalStageCount) * 100) : 0;
                  return (
                    <Link
                      key={p.id}
                      to={`/projects/${p.id}`}
                      className="block p-3 rounded-lg bg-[var(--bg-secondary)] hover:bg-[var(--bg-primary)] transition-colors group"
                    >
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <p className="text-sm font-medium group-hover:underline truncate min-w-0">{p.name}</p>
                        <Badge variant="outline" className={`text-[10px] flex-shrink-0 ${stageColors[p.stage] || ''}`}>
                          {p.stage}
                        </Badge>
                      </div>
                      {/* Progress bar */}
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-[var(--border-color)] overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{
                              width: `${progress}%`,
                              backgroundColor: accentColor,
                            }}
                          />
                        </div>
                        <span className="text-[10px] text-[var(--text-secondary)] whitespace-nowrap">
                          {progress}%
                        </span>
                      </div>
                      <p className="text-[10px] text-[var(--text-secondary)] mt-1">
                        Stage {Math.min(stageIndex + 1, totalStageCount)} of {totalStageCount}
                      </p>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <FolderKanban size={48} className="mx-auto mb-4 text-[var(--text-secondary)]" />
                <p className="text-[var(--text-secondary)] mb-1">{t('dashboard.noActiveProjects')}</p>
                <p className="text-sm text-[var(--text-secondary)]">{t('dashboard.noActiveProjectsDesc')}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: accentColor }} />
              <CardTitle>{t('dashboard.quickActions')}</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button variant="outline" className="w-full gap-2 justify-start" asChild>
              <Link to="/tickets">
                <Ticket size={16} />
                {t('dashboard.submitRequest')}
              </Link>
            </Button>
            <Button variant="outline" className="w-full gap-2 justify-start" asChild>
              <Link to="/invoices">
                <Receipt size={16} />
                {t('dashboard.viewInvoices')}
              </Link>
            </Button>
            <Button variant="outline" className="w-full gap-2 justify-start" asChild>
              <Link to="/projects">
                <FolderKanban size={16} />
                {t('projects.title')}
              </Link>
            </Button>
            <div className="pt-3 border-t border-[var(--border-color)]">
              <Link to="/profile" className="flex items-center gap-2 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
                <Settings size={14} />
                Account settings
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Site Health widget */}
      <SiteHealthWidget />
    </>
  );
}

function SiteHealthWidget() {
  const { data, isLoading } = useSiteMonitors();
  const monitors = data?.data || [];

  if (isLoading || monitors.length === 0) return null;

  const activeMonitors = monitors.filter((m: any) => m.isActive);
  if (activeMonitors.length === 0) return null;

  const criticalCount = activeMonitors.filter((m: any) =>
    (m.checks || []).some((c: any) => c.status === 'fail')
  ).length;

  return (
    <Card className="mt-6">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <HeartPulse size={16} className="text-dyndev-green" />
          <CardTitle className="text-sm">Site Health</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-[var(--text-secondary)]">
              {activeMonitors.length} site{activeMonitors.length !== 1 ? 's' : ''} monitored
            </p>
            {criticalCount > 0 && (
              <p className="text-xs text-red-400 mt-0.5 flex items-center gap-1">
                <Activity size={10} />
                {criticalCount} need{criticalCount === 1 ? 's' : ''} attention
              </p>
            )}
          </div>
          <Link
            to="/projects"
            className="text-xs text-dyndev-green hover:underline flex items-center gap-1"
          >
            View Details <ArrowRight size={10} />
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
